import React, { useEffect, useState } from 'react';
import { X, Copy, Star, Search } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import decorationItems from './decorationItems';

const Modal = ({ isOpen, onClose, title, fancyText }) => {
     useTheme();
  const [decoratedText, setDecoratedText] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);

  // Categories with emoji icons
  const categories = [
    { id: 'basic', name: 'Basic', emoji: '🔤' },
    { id: 'box', name: 'Boxes', emoji: '📦' },
    { id: 'arrow', name: 'Arrows', emoji: '➡️' },
    { id: 'star', name: 'Stars', emoji: '⭐' },
    { id: 'heart', name: 'Hearts', emoji: '❤️' },
    { id: 'music', name: 'Music', emoji: '🎵' },
    { id: 'flower', name: 'Flowers', emoji: '🌸' },
    { id: 'weather', name: 'Weather', emoji: '☀️' },
    { id: 'animal', name: 'Animals', emoji: '🐶' },
    { id: 'food', name: 'Food', emoji: '🍎' },
    { id: 'holiday', name: 'Holiday', emoji: '🎄' },
    { id: 'tech', name: 'Tech', emoji: '📱' },
    { id: 'transport', name: 'Transport', emoji: '🚗' },
    { id: 'sport', name: 'Sports', emoji: '⚽' },
    { id: 'game', name: 'Games', emoji: '🎮' }
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setDecoratedText(fancyText);
      const savedFavorites = localStorage.getItem('fancyTextFavorites');
      if (savedFavorites) {
        setFavorites(JSON.parse(savedFavorites));
      }
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, fancyText]);

  const handleDecorate = (decorator) => {
    const newDecoratedText = decorator(fancyText);
    setDecoratedText(newDecoratedText);
    setCopied(false);
  };

  const copyToClipboard = () => {
    if (!decoratedText) return;
    navigator.clipboard.writeText(decoratedText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const toggleFavorite = (text) => {
    if (!text) return;
    const newFavorites = favorites.includes(text)
      ? favorites.filter(fav => fav !== text)
      : [...favorites, text];
    setFavorites(newFavorites);
    localStorage.setItem('fancyTextFavorites', JSON.stringify(newFavorites));
  };

  const getFilteredItems = () => {
    if (activeTab === 'favorites') {
      return favorites.map(text => ({
        text,
        preview: text,
        apply: () => setDecoratedText(text)
      }));
    }

    // Get the array of decorators for the current category
    const categoryDecorators = decorationItems[activeTab] || [];
    
    // Map each decorator function to an object with preview and decorator
    return categoryDecorators
      .map(decorator => ({
        decorator,
        preview: decorator(fancyText || 'Sample')
      }))
      .filter(item => 
        item.preview.toLowerCase().includes(searchQuery.toLowerCase())
      );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1000]">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl w-[90%] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col text-gray-800 dark:text-gray-200">
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 -m-6 mb-4 rounded-t-xl text-white flex justify-between items-center">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="text-gray-400 dark:text-gray-500" size={16} />
            </div>
            <input
              type="text"
              placeholder="Search decorations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
              aria-label="Search decorations"
            />
          </div>
          <button
            onClick={copyToClipboard}
            disabled={!decoratedText}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              !decoratedText 
                ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' 
                : 'bg-green-500 dark:bg-green-600 hover:bg-green-600 dark:hover:bg-green-700 text-white'
            }`}
            aria-label="Copy to clipboard"
          >
            <Copy size={16} />
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        {/* Preview section */}
        <div className="p-4 mb-4 rounded-lg border bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600">
          <div className="flex justify-between items-start gap-2">
            <div className="text-lg break-words whitespace-pre-line min-h-8 text-gray-800 dark:text-gray-200">
              {decoratedText || <span className="text-gray-400 dark:text-gray-500">Preview will appear here</span>}
            </div>
            {decoratedText && (
              <button
                onClick={() => toggleFavorite(decoratedText)}
                className={`p-1 rounded-full flex-shrink-0 ${
                  favorites.includes(decoratedText)
                    ? 'text-yellow-500'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
                aria-label={favorites.includes(decoratedText) ? "Remove from favorites" : "Add to favorites"}
              >
                <Star size={16} fill={favorites.includes(decoratedText) ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>
        </div>

        {/* Categories tabs */}
        <div className="relative mb-4">
          <div className="flex space-x-1  pb-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('favorites')}
              className={`flex-shrink-0 border border-green-500 dark:border-green-600 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                activeTab === 'favorites'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
              }`}
              id="tab-favorites"
            >
              <Star size={14} fill={activeTab === 'favorites' ? 'currentColor' : 'none'} />
              Favorites
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`flex-shrink-0 border border-green-500 dark:border-green-600 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === category.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => {
                  setActiveTab(category.id);
                  // Optional: Scroll the active tab into view
                  document.getElementById(`tab-${category.id}`)?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                  });
                }}
                id={`tab-${category.id}`}
              >
                <span className="mr-1.5">{category.emoji}</span>
                {category.name}
              </button>
            ))}
          </div>
          {/* Custom scrollbar indicator */}
          <div className=" bg-gray-200 dark:bg-gray-700 w-full relative">
            <div className="h-full  dark:bg-blue-400 transition-all duration-300" 
                 style={{ width: '20%', marginLeft: `${(categories.findIndex(c => c.id === activeTab) / categories.length) * 100}%` }} />
          </div>
        </div>

        {/* Decorations grid */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {getFilteredItems().length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {getFilteredItems().map((item, index) => (
                <button
                  key={index}
                  onClick={() => item.decorator ? handleDecorate(item.decorator) : item.apply()}
                  className="p-3 rounded-lg border transition-colors text-left h-full bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
                  aria-label={`Apply decoration`}
                >
                  <div className="text-sm break-words">{item.preview}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500">
              {activeTab === 'favorites' ? (
                <>
                  <Star size={24} className="mb-2" />
                  <p>No favorites yet</p>
                  <p className="text-sm">Click the star to save decorations</p>
                </>
              ) : (
                <>
                  <Search size={24} className="mb-2" />
                  <p>No decorations found</p>
                  <p className="text-sm">Try a different search term</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;